import React, { useState, useEffect } from "react";
import { TreeSelect } from "antd";
import PropTypes from "prop-types";
import api from "../../../core/api/config";
import Swal from "sweetalert2";

const VincularOrganogramaModal = ({
  show,
  onClose,
  grupoId,
  dados,
  vinculosExistentes,
}) => {
  const [value, setValue] = useState([]);
  const [vinculados, setVinculados] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingTabela, setLoadingTabela] = useState(false);

  const listarVinculos = async (pagina = 0) => {
    if (!grupoId) return;

    try {
      setLoadingTabela(true);

      const { data } = await api.get(
        `/api/tracking/grupo-organograma/grupo/${grupoId}`,
        {
          params: { page: pagina, size, sort: "id" },
        },
      );

      setVinculados(data.content || []);
      setTotalPages(data.totalPages || 0);
      setPage(pagina);
    } catch {
      Swal.fire("Erro", "Falha ao carregar vínculos", "error");
    } finally {
      setLoadingTabela(false);
    }
  };

  useEffect(() => {
    if (show) listarVinculos(0);
  }, [show, grupoId]);

  /* CARREGAR VÍNCULOS JÁ SALVOS */
  useEffect(() => {
    setVinculados(vinculosExistentes || []);
  }, [vinculosExistentes]);

  if (!show) return null;

  /* =========================
     MAPA DE NÓS DA ÁRVORE
  ========================= */
  const nodeMap = {};
  const mapNodes = (nodes) => {
    nodes.forEach((n) => {
      nodeMap[n.idOrganograma] = n;
      if (n.filhos?.length) mapNodes(n.filhos);
    });
  };
  mapNodes(dados);

  /* =========================
     ADICIONAR À TABELA
  ========================= */
  /* =========================
     SALVAR NO BACKEND
  ========================= */
  const handleVincular = async () => {
    const novos = value
      .map((item) => item.value)
      .filter((id) => !vinculados.some((v) => v.idOrganograma === Number(id)))
      .map((id) => {
        const node = nodeMap[id];
        return {
          grupoAcessoId: grupoId,
          idOrganograma: node.idOrganograma,
          descricaoOrganograma: node.descricao,
          nivelAcessoOrganograma: node.cnes || "",
        };
      });

    try {
      await Promise.all(
        novos.map((item) => api.post("/api/tracking/grupo-organograma", item)),
      );

      await listarVinculos(page);
      setValue([]);
      Swal.fire("Vinculado!", "Organograma vinculado com sucesso", "success");
    } catch {
      Swal.fire("Erro", "Falha ao vincular", "error");
    }
  };

  /* =========================
     REMOVER
  ========================= */
  const removerVinculo = async (idOrganograma) => {
    const confirmar = await Swal.fire({
      title: "Remover vínculo?",
      text: "Essa ação irá desvincular o organograma do grupo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!confirmar.isConfirmed) return;

    try {
      await api.delete("/api/tracking/grupo-organograma", {
        params: {
          grupoAcessoId: grupoId,
          idOrganograma: idOrganograma,
        },
      });

      await listarVinculos(page);

      Swal.fire("Removido!", "Vínculo excluído com sucesso", "success");
    } catch (error) {
      Swal.fire("Erro", "Falha ao remover vínculo", "error");
    }
  };

  /* =========================
     ÁRVORE HIERÁRQUICA
  ========================= */
  const buildTree = (nodes = []) =>
    nodes.map((node) => ({
      title: `${node.descricao}${node.cnes ? ` (${node.cnes})` : ""}`,
      value: node.idOrganograma,
      key: node.idOrganograma,
      children: node.filhos?.length ? buildTree(node.filhos) : [],
    }));

  const treeData = buildTree(dados);

  return (
    <div className="modal show d-block" tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4>Vincular Organograma — Grupo {grupoId}</h4>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <TreeSelect
              style={{ width: "100%" }}
              value={value}
              treeData={treeData}
              placeholder="Selecione os organogramas"
              treeCheckable
              treeCheckStrictly
              showSearch
              treeNodeFilterProp="title"
              filterTreeNode={(input, node) =>
                node.title.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(vals) => setValue(vals)}
              treeDefaultExpandAll
              getPopupContainer={(trigger) => trigger.parentNode}
            />

            <button
              className="btn btn-primary my-3"
              onClick={handleVincular}
              disabled={value.length === 0}
            >
              <i className="fas fa-link me-2" />
              Vincular
            </button>

            <h6 className="mb-2">Organogramas Vinculados</h6>

            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover align-middle">
                <thead className="thead-light">
                  <tr>
                    <th>Grupo</th>
                    <th>Organograma</th>
                    <th width="40"></th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTabela && (
                    <tr>
                      <td colSpan="3" className="text-center text-muted">
                        Carregando...
                      </td>
                    </tr>
                  )}

                  {!loadingTabela && vinculados.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center text-muted">
                        Nenhum vínculo encontrado
                      </td>
                    </tr>
                  )}

                  {!loadingTabela &&
                    vinculados.map((item) => (
                      <tr key={item.idOrganograma}>
                        <td>
                          {item.grupoAcessoId} - {item.nomeGrupo}
                        </td>
                        <td>
                          {item.idOrganograma} - {item.descricaoOrganograma}
                        </td>
                        <td>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removerVinculo(item.idOrganograma)}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === 0}
                  onClick={() => listarVinculos(page - 1)}
                >
                  ← Anterior
                </button>

                <span>
                  Página {page + 1} de {totalPages}
                </span>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => listarVinculos(page + 1)}
                >
                  Próxima →
                </button>
              </div>
            </div>
          </div>

          <div className="modal-footer border-top d-grid gap-2 d-md-flex justify-content-end">
            <button className="btn btn-secondary" onClick={onClose}>
              <i className="feather-x me-2"></i>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

VincularOrganogramaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  grupoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dados: PropTypes.array,
  vinculosExistentes: PropTypes.array,
};

export default VincularOrganogramaModal;
